import i18next from 'i18next';
import React from 'react';
import { PartialDeep } from 'type-fest';

import { CerbosFeature } from '../../services/cerbos/features';
import { assertNever } from '../../utils/variables';
import { isSharedViewerUrl } from '../routing/utils';
import { matchesRoutePattern } from '../utils/route-pattern';
import { useOptionalUserState } from './user-state/user-state';

// Export types for use by ThemeManager
type ProductionDomains =
  | 'portalen.skymap.se'
  | 'skymap-portal.com'
  | 'moba.skymap.se'
  | 'moba-de.skymap-portal.com'
  | 'cf-portalen.skymap.se'
  | 'cf-moba.skymap.se'
  | 'makin.skymap-portal.com';
type DevelopmentDomains =
  | 'development.skymap.se'
  | 'development.skymap-portal.com'
  | 'mobadev.skymap.se'
  | 'moba-de-dev.skymap-portal.com'
  | 'cf-development.skymap.se'
  | 'cf-mobadev.skymap.se'
  | 'makin-development.skymap-portal.com';
type StagingDomains =
  | 'staging.skymap.se'
  | 'staging.skymap-portal.com'
  | 'moba-staging.skymap.se'
  | 'moba-staging.skymap-portal.com'
  | 'moba-de-staging.skymap-portal.com'
  | 'cf-staging.skymap.se'
  | 'cf-moba-staging.skymap.se'
  | 'makin-staging.skymap-portal.com';
type LocalDomains =
  | 'moba-de.local'
  | 'moba.local'
  | 'dev.local'
  | 'dev.localhost'
  | 'localhost'
  | 'moba-de.localhost'
  | 'moba.localhost'
  | 'makin.localhost'
  | 'makin-david.skymap-portal.com';

export type AvailableDomains =
  | ProductionDomains
  | DevelopmentDomains
  | LocalDomains
  | StagingDomains;

export type IntercomApp = {
  id: string;
  apiBase: string;
};

export interface Theme {
  baseDir: string;
  themeName: 'default' | 'moba' | 'makin';
  productName: string;
  intercomApp: IntercomApp;
}

export type ThemeSettings = PartialDeep<{
  routes: {
    '/access-tokens': boolean;
    '/accounts': boolean;
    '/jobs': boolean;
    '/projects': boolean;
    '/users': boolean;
    '/registercompany': boolean;
    '/register-design': boolean;
    '/companies': boolean;
    '/companies/:companyId': boolean;
    '/companies/:companyId/admins': boolean;
    '/companies/:companyId/licenses': boolean;
    '/projects/:projectId': boolean;
    '/projects/:projectId/email': boolean;
    '/projects/:projectId/filemanager': boolean;
    '/projects/:projectId/geodata': boolean;
    '/projects/:projectId/geodata/:geodataId/viewer': boolean;
    '/projects/:projectId/integrations': boolean;
    '/projects/:projectId/users': boolean;
    '/projects/:projectId/settings': boolean;
    '/projects/:projectId/timeline': boolean;
    '/projects/:projectId/transfer': boolean;
    '/projects/:projectId/viewer': boolean;
    '/shared-folder/:id': boolean;
    '/profile': boolean;
  };
  skyview: {
    toolbar: {
      centerViewVisible: boolean;
      commentVisible: boolean;
      profileSectionVisible: boolean;
      compareOrthophotosVisible: boolean;
      inspectOriginalPhotosVisible: boolean;
      lightVisible: boolean;
      measureAdvancedVisible: boolean;
      measureVisible: boolean;
      occlusionCullingVisible: boolean;
      orthoSceneVisible: boolean;
      perspectiveSceneVisible: boolean;
      pointCloudSceneVisible: boolean;
      referencePlaneVisible: boolean;
      intersectionVisible: boolean;
      rotateVisible: boolean;
      scaleVisible: boolean;
      selectVisible: boolean;
      skyMapDesignVisible: boolean;
      timelinesVisible: boolean;
      wmsVisible: boolean;
      translateVisible: boolean;
      volumeCalculationVisible: boolean;
    };
    chips: {
      shareVisible: boolean;
      asBuiltVisible: boolean;
    };
    wms: {
      layers: {
        satellite: boolean;
      };
    };
  };
  fileManager: {
    actionBar: {
      connectVisible: boolean;
      folderTagsVisible: boolean;
      ConnectFolderVisible: boolean;
    };
  };
  other: {
    contextMenuVisible: boolean;
    intercomWidgetVisible: boolean;
    sharedViewTopBarVisible: boolean;
    supportIconVisible: boolean;
  };
}>;

export interface ThemeContextValue {
  theme: Theme;
  settings: ThemeSettings;
  isSharedView: boolean;
  setSharedView: (isSharedView: boolean) => void;
  hasAccessToRoute: (routePath: string) => boolean;
  hasAccessToFeature: (cerbosKey: CerbosFeature) => boolean;
  isSwedishDomain: () => boolean;
  isMobaTheme: () => boolean;
  isMakinTheme: () => boolean;
  getThemeName: () => 'default' | 'moba' | 'makin';
  getTermsOfServiceUrl: () => string;
  isSwedishLanguageSelected: () => boolean;
  getCloudProcessingTermsOfServiceUrl: () => string;
  getOnDemandPriceListUrl: () => string;
  getHelpCenterUrl: () => string;
  getLogoTypeUrl: () => string;
  getFullPageBoxBackground: () => string;
  getFullPageBoxBackgroundSize: () => string;
  getShellBackgroundColor: () => string;
  getTopMenuBackgroundColor: () => string;
  getTopMenuLogotype: () => string;
  getTopMenuTextColor: () => string;
  getTopMenuTextHoverColor: () => string;
  getHostname: () => AvailableDomains;
  getProductName: () => string;
  getIntercomApp: () => IntercomApp;
  hostnameStartsWith: (value: string) => boolean;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeStateProvider');
  }
  return context;
};

type Props = {
  children: React.ReactNode;
  isSharedView?: boolean;
};

// Export helper functions for use by ThemeManager
function getHostname(): AvailableDomains {
  return window.location.hostname as AvailableDomains;
}

function getThemeName(): 'default' | 'moba' | 'makin' {
  switch (getHostname()) {
    // Moba
    case 'moba.local':
    case 'moba.skymap.se':
    case 'cf-moba.skymap.se':
    case 'mobadev.skymap.se':
    case 'cf-mobadev.skymap.se':
    case 'moba-de-dev.skymap-portal.com':
    case 'moba-de.skymap-portal.com':
    case 'moba-staging.skymap.se':
    case 'moba-de-staging.skymap-portal.com':
    case 'moba-staging.skymap-portal.com':
    case 'cf-moba-staging.skymap.se':
    case 'moba-de.local':
    case 'moba-de.localhost':
    case 'moba.localhost':
      return 'moba';

    // Makin (same access as SkyMap)
    case 'makin.localhost':
    case 'makin-david.skymap-portal.com':
    case 'makin.skymap-portal.com':
    case 'makin-development.skymap-portal.com':
    case 'makin-staging.skymap-portal.com':
      return 'makin';

    // SkyMap
    default:
      return 'default';
  }
}

export function getProductName(): string {
  switch (getThemeName()) {
    case 'moba':
      return 'MOBA Design Tool';
    case 'makin':
      return 'Makin';
    default:
      return 'SkyMap Portal';
  }
}

function getIntercomApp(): IntercomApp {
  switch (getThemeName()) {
    case 'moba':
      switch (getHostname()) {
        case 'moba.skymap.se':
        case 'cf-moba.skymap.se':
          return { id: 'l3y5v1c7', apiBase: 'https://api-iam.intercom.io' };
        case 'moba-de.skymap-portal.com':
          return { id: 'mt2mwi41', apiBase: 'https://api-iam.intercom.io' };
        default:
          return { id: '', apiBase: 'https://api-iam.intercom.io' };
      }
    case 'makin':
      switch (getHostname()) {
        case 'makin.skymap-portal.com':
        case 'makin-development.skymap-portal.com':
        case 'makin-staging.skymap-portal.com':
        case 'makin-david.skymap-portal.com':
          return { id: 'z4kpr0l0', apiBase: 'https://api-iam.eu.intercom.io' };
        default:
          return { id: '', apiBase: 'https://api-iam.intercom.io' };
      }
    default:
      switch (getHostname()) {
        case 'dev.local':
        case 'dev.localhost':
        case 'development.skymap.se':
        case 'staging.skymap.se':
        case 'cf-development.skymap.se':
        case 'development.skymap-portal.com':
        case 'staging.skymap-portal.com':
          return { id: 'tgg1ocnw', apiBase: 'https://api-iam.eu.intercom.io' };
        case 'portalen.skymap.se':
        case 'cf-portalen.skymap.se':
        case 'skymap-portal.com':
          return { id: 'z4kpr0l0', apiBase: 'https://api-iam.eu.intercom.io' };
        default:
          return { id: '', apiBase: 'https://api-iam.intercom.io' };
      }
  }
}

export function initSettings(
  isSharedView: boolean,
  themeName: 'default' | 'moba' | 'makin',
): ThemeSettings {
  switch (themeName) {
    case 'default':
    case 'makin':
      return {
        routes: {
          '/access-tokens': true,
          '/accounts': true,
          '/jobs': true,
          '/projects': true,
          '/users': true,
          '/registercompany': true,
          '/register-design': true,
          '/companies': true,
          '/companies/:companyId': true,
          '/companies/:companyId/admins': true,
          '/companies/:companyId/licenses': true,
          '/projects/:projectId': true,
          '/projects/:projectId/email': true,
          '/projects/:projectId/filemanager': true,
          '/projects/:projectId/geodata': true,
          '/projects/:projectId/geodata/:geodataId/viewer': true,
          '/projects/:projectId/integrations': true,
          '/projects/:projectId/users': true,
          '/projects/:projectId/settings': true,
          '/projects/:projectId/timeline': true,
          '/projects/:projectId/transfer': true,
          '/projects/:projectId/viewer': true,
          '/shared-folder/:id': true,
          '/profile': true,
        },
        skyview: {
          toolbar: {
            centerViewVisible: true,
            commentVisible: !isSharedView,
            profileSectionVisible: !isSharedView,
            compareOrthophotosVisible: !isSharedView,
            inspectOriginalPhotosVisible: !isSharedView,
            lightVisible: true,
            measureAdvancedVisible: true,
            measureVisible: true,
            occlusionCullingVisible: true,
            orthoSceneVisible: true,
            perspectiveSceneVisible: true,
            pointCloudSceneVisible: true,
            referencePlaneVisible: true,
            intersectionVisible: true,
            rotateVisible: !isSharedView,
            scaleVisible: !isSharedView,
            selectVisible: true,
            skyMapDesignVisible: true,
            timelinesVisible: true,
            wmsVisible: true,
            translateVisible: !isSharedView,
            volumeCalculationVisible: !isSharedView,
          },
          chips: {
            shareVisible: true,
            asBuiltVisible: true,
          },
          wms: {
            layers: {
              satellite: true,
            },
          },
        },
        fileManager: {
          actionBar: {
            connectVisible: true,
            folderTagsVisible: true,
            ConnectFolderVisible: true,
          },
        },
        other: {
          intercomWidgetVisible: true,
          sharedViewTopBarVisible: true,
          supportIconVisible: true,
        },
      };

    case 'moba':
      return {
        routes: {
          '/projects': true,
          '/companies': true,
          '/companies/:companyId': true,
          '/companies/:companyId/admins': true,
          '/companies/:companyId/licenses': true,
          '/registercompany': true,
          '/register-design': true,
          '/projects/:projectId': true,
          '/projects/:projectId/email': false,
          '/projects/:projectId/filemanager': true,
          '/projects/:projectId/integrations': true,
          '/projects/:projectId/users': true,
          '/projects/:projectId/settings': true,
          '/projects/:projectId/viewer': true,
          '/shared-folder/:id': true,
          '/profile': true,
        },
        skyview: {
          toolbar: {
            commentVisible: true,
            profileSectionVisible: true,
            scaleVisible: true,
            selectVisible: true,
            skyMapDesignVisible: true,
            volumeCalculationVisible: true,
            lightVisible: true,
            wmsVisible: true,
            rotateVisible: true,
            translateVisible: true,
            intersectionVisible: true,
          },
          chips: {
            asBuiltVisible: true,
          },
          wms: {
            layers: {
              satellite: true,
            },
          },
        },
        fileManager: {
          actionBar: {
            connectVisible: true,
            folderTagsVisible: true,
            ConnectFolderVisible: true,
          },
        },
        other: {
          intercomWidgetVisible: true,
          sharedViewTopBarVisible: true,
        },
      };
  }
}

// Global reference to React theme state (set by ThemeStateProvider)
// This is used by ThemeManager to access React state from non-React code
let reactThemeState: ThemeContextValue | null = null;

/**
 * Sets the React theme state reference. Called by ThemeStateProvider on mount.
 * This allows ThemeManager (non-React code) to access the React state.
 */
export function setReactThemeState(state: ThemeContextValue | null) {
  reactThemeState = state;
}

/**
 * Gets the current React theme state. Used by ThemeManager.
 * Throws an error if theme state is not initialized (ThemeStateProvider must be mounted).
 */
export function getReactThemeState(): ThemeContextValue {
  if (!reactThemeState) {
    throw new Error(
      'Theme state is not initialized. ThemeStateProvider must be mounted before accessing ThemeManager.',
    );
  }
  return reactThemeState;
}

export const ThemeStateProvider = ({
  children,
  isSharedView: initialSharedView = false,
}: Props) => {
  const userState = useOptionalUserState();
  const isOrganizationAdmin = userState?.isOrganizationAdmin ?? false;

  // Track window.location.pathname to detect route changes
  const [windowPathname, setWindowPathname] = React.useState(window.location.pathname);

  // Use prop if provided, otherwise detect from URL
  const [isSharedView, setIsSharedView] = React.useState(initialSharedView || isSharedViewerUrl());

  // Listen for window.location.pathname changes
  React.useEffect(() => {
    const checkPathname = () => {
      const currentPathname = window.location.pathname;
      if (currentPathname !== windowPathname) {
        setWindowPathname(currentPathname);
      }
    };

    // Check immediately and then poll, since pathname changes outside this provider
    // don't emit an event we can subscribe to here.
    checkPathname();
    const interval = setInterval(checkPathname, 100);

    return () => clearInterval(interval);
  }, [windowPathname]);

  // Update isSharedView when route changes
  React.useEffect(() => {
    const currentIsSharedView = initialSharedView || isSharedViewerUrl();
    setIsSharedView(currentIsSharedView);
  }, [initialSharedView, windowPathname]);

  const themeName = React.useMemo(() => getThemeName(), []);
  const theme = React.useMemo<Theme>(
    () => ({
      baseDir: '/static/themes',
      themeName,
      productName: getProductName(),
      intercomApp: getIntercomApp(),
    }),
    [themeName],
  );

  const settings = React.useMemo(
    () => initSettings(isSharedView, themeName),
    [isSharedView, themeName],
  );

  // Initialize DOM attributes on mount
  React.useEffect(() => {
    document.getElementsByTagName('html')[0].setAttribute('data-theme', theme.themeName);
    document.title = theme.productName;

    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/x-icon';
    link.href = `${theme.baseDir}/${theme.themeName}/images/favicon.ico`;
    document.head.appendChild(link);
  }, [theme]);

  const hasAccessToRoute = React.useCallback(
    (routePath: string): boolean => {
      if (isOrganizationAdmin) {
        return true;
      }

      // Check if any route pattern matches
      const routeSettings = settings.routes ?? {};
      for (const [pattern, enabled] of Object.entries(routeSettings)) {
        if (matchesRoutePattern(pattern, routePath) && enabled) {
          return true;
        }
      }

      return false;
    },
    [isOrganizationAdmin, settings],
  );

  const hasAccessToFeature = React.useCallback(
    (cerbosKey: CerbosFeature): boolean => {
      const resolveCerbosKey = () => {
        switch (cerbosKey) {
          case 'feature:viewer:toolbar:comment':
            return settings.skyview?.toolbar?.commentVisible;
          case 'feature:viewer:toolbar:focus':
            return settings.skyview?.toolbar?.centerViewVisible;
          case 'feature:viewer:toolbar:select':
            return settings.skyview?.toolbar?.selectVisible;
          case 'feature:viewer:toolbar:translate':
            return settings.skyview?.toolbar?.translateVisible;
          case 'feature:viewer:toolbar:rotate':
            return settings.skyview?.toolbar?.rotateVisible;
          case 'feature:viewer:toolbar:scale':
            return settings.skyview?.toolbar?.scaleVisible;
          case 'feature:viewer:toolbar:light':
            return settings.skyview?.toolbar?.lightVisible;
          case 'feature:viewer:toolbar:volume_calculation':
            return settings.skyview?.toolbar?.volumeCalculationVisible;
          case 'feature:viewer:toolbar:measurement':
            return settings.skyview?.toolbar?.measureVisible;
          case 'feature:viewer:toolbar:measurement:advanced':
            return settings.skyview?.toolbar?.measureAdvancedVisible;
          case 'feature:viewer:toolbar:design':
            return settings.skyview?.toolbar?.skyMapDesignVisible;
          case 'feature:viewer:toolbar:tiled_model_scene':
            return settings.skyview?.toolbar?.perspectiveSceneVisible;
          case 'feature:viewer:toolbar:ortho_tile_scene':
            return settings.skyview?.toolbar?.orthoSceneVisible;
          case 'feature:viewer:toolbar:point_cloud_scene':
            return settings.skyview?.toolbar?.pointCloudSceneVisible;
          case 'feature:viewer:toolbar:profile_section':
            return settings.skyview?.toolbar?.profileSectionVisible;
          case 'feature:viewer:toolbar:compare_ortho_photos':
            return settings.skyview?.toolbar?.compareOrthophotosVisible;
          case 'feature:viewer:toolbar:original_photos':
            return settings.skyview?.toolbar?.inspectOriginalPhotosVisible;
          case 'feature:viewer:toolbar:occlusion_culling':
            return settings.skyview?.toolbar?.occlusionCullingVisible;
          case 'feature:viewer:toolbar:reference_plane':
            return settings.skyview?.toolbar?.referencePlaneVisible;
          case 'feature:viewer:toolbar:intersection':
            return settings.skyview?.toolbar?.intersectionVisible;
          case 'feature:viewer:toolbar:timeline':
            return settings.skyview?.toolbar?.timelinesVisible;
          case 'feature:viewer:toolbar:wms':
            return settings.skyview?.toolbar?.wmsVisible;
          case 'feature:viewer:wms:layers:satellite':
            return settings.skyview?.wms?.layers?.satellite;
          case 'feature:viewer:chips:share_viewer':
            return settings.skyview?.chips?.shareVisible;
          case 'feature:viewer:chips:as_built':
            return settings.skyview?.chips?.asBuiltVisible;
          case 'feature:shared_viewer:context_menu':
            return settings.other?.contextMenuVisible;
          case 'feature:global:intercom:widget':
            return settings.other?.intercomWidgetVisible;
          case 'feature:global:intercom:help_center':
            return settings.other?.supportIconVisible;
          case 'feature:file_manager:share_folder':
            return settings.fileManager?.actionBar?.connectVisible;
          case 'feature:file_manager:tags':
            return settings.fileManager?.actionBar?.folderTagsVisible;
          case 'feature:file_manager:connect_folder':
            return settings.fileManager?.actionBar?.ConnectFolderVisible;
          default:
            assertNever(cerbosKey);
        }
      };

      return resolveCerbosKey() ?? false;
    },
    [settings],
  );

  const isSwedishDomain = React.useCallback((): boolean => {
    switch (getHostname()) {
      case 'moba-de-dev.skymap-portal.com':
      case 'moba-de-staging.skymap-portal.com':
      case 'moba-de.skymap-portal.com':
      case 'moba-de.local':
        return false;
      default:
        return true;
    }
  }, []);

  const getTermsOfServiceUrl = React.useCallback((): string => {
    const isMoba = themeName === 'moba';
    return isMoba
      ? 'https://skymap.se/en/agreement-moba-design-tool'
      : i18next.language === 'sv'
        ? 'https://skymap.se/villkor-portal'
        : 'https://skymap.se/en/terms-portal';
  }, [themeName]);

  const getCloudProcessingTermsOfServiceUrl = React.useCallback((): string => {
    return i18next.language === 'sv'
      ? 'https://www.skymap.se/villkor-molnprocessering'
      : 'https://www.skymap.se/en/terms-cloud-processing';
  }, []);

  const getOnDemandPriceListUrl = React.useCallback((): string => {
    return 'https://skymap.se/priser';
  }, []);

  const getHelpCenterUrl = React.useCallback((): string => {
    const hostname = getHostname();
    if (hostname.startsWith('moba-de')) {
      return 'https://intercom.help/moba-design-tool-de/en';
    }
    if (themeName === 'moba') {
      return 'https://intercom.help/moba-design-tool/sv';
    }
    return 'https://intercom-help.eu/skymap/sv';
  }, [themeName]);

  const getLogoTypeUrl = React.useCallback((): string => {
    switch (themeName) {
      case 'moba':
        return '/static/themes/moba/images/logotype.png';
      case 'makin':
        return '/static/themes/makin/images/makin-logo-1.svg';
      default:
        return '/static/themes/default/images/logotype.png';
    }
  }, [themeName]);

  const getFullPageBoxBackground = React.useCallback((): string => {
    switch (themeName) {
      case 'moba':
        return "url('/static/themes/moba/images/background.png') bottom left fixed no-repeat rgba(0, 0, 0, 0.8)";
      case 'makin':
        return "url('/static/themes/makin/images/makin_background.jpeg') center center no-repeat";
      default:
        return "url('/static/themes/default/images/background.jpg') center center no-repeat";
    }
  }, [themeName]);

  const getFullPageBoxBackgroundSize = React.useCallback((): string => {
    switch (themeName) {
      case 'moba':
        return 'none';
      default:
        return 'cover';
    }
  }, [themeName]);

  const getShellBackgroundColor = React.useCallback((): string => {
    switch (themeName) {
      case 'makin':
        return '#f2f0eb';
      case 'moba':
      default:
        return '#f5f6f8';
    }
  }, [themeName]);

  const getTopMenuBackgroundColor = React.useCallback((): string => {
    switch (themeName) {
      case 'moba':
        return '#fc0';
      case 'makin':
        return '#000';
      default:
        return '#5e6a71';
    }
  }, [themeName]);

  const getTopMenuLogotype = React.useCallback((): string => {
    switch (themeName) {
      case 'moba':
        return '/static/themes/moba/images/logotype.png';
      case 'makin':
        return '/static/themes/makin/images/makin-logo-neg.svg';
      default:
        return '/static/assets/img/navbar_logo.png';
    }
  }, [themeName]);

  const getTopMenuTextColor = React.useCallback((): string => {
    switch (themeName) {
      case 'moba':
        return '#000';
      default:
        return '#fff';
    }
  }, [themeName]);

  const getTopMenuTextHoverColor = React.useCallback((): string => {
    switch (themeName) {
      case 'moba':
        return '#fff';
    }
    return '#fff';
  }, [themeName]);

  const hostnameStartsWith = React.useCallback((value: string): boolean => {
    return getHostname().startsWith(value);
  }, []);

  const value: ThemeContextValue = React.useMemo(() => {
    const themeValue: ThemeContextValue = {
      theme,
      settings,
      isSharedView,
      setSharedView: setIsSharedView,
      hasAccessToRoute,
      hasAccessToFeature,
      isSwedishDomain,
      isMobaTheme: () => themeName === 'moba',
      isMakinTheme: () => themeName === 'makin',
      getThemeName: () => themeName,
      getTermsOfServiceUrl,
      isSwedishLanguageSelected: () => i18next.language === 'sv',
      getCloudProcessingTermsOfServiceUrl,
      getOnDemandPriceListUrl,
      getHelpCenterUrl,
      getLogoTypeUrl,
      getFullPageBoxBackground,
      getFullPageBoxBackgroundSize,
      getShellBackgroundColor,
      getTopMenuBackgroundColor,
      getTopMenuLogotype,
      getTopMenuTextColor,
      getTopMenuTextHoverColor,
      getHostname,
      getProductName: () => theme.productName,
      getIntercomApp: () => theme.intercomApp,
      hostnameStartsWith,
    };
    // Set global reference synchronously during render so it's available for non-React code
    // This is safe because we're only reading from the value, not mutating it
    setReactThemeState(themeValue);
    return themeValue;
  }, [
    theme,
    settings,
    isSharedView,
    hasAccessToRoute,
    hasAccessToFeature,
    isSwedishDomain,
    themeName,
    getTermsOfServiceUrl,
    getCloudProcessingTermsOfServiceUrl,
    getOnDemandPriceListUrl,
    getHelpCenterUrl,
    getLogoTypeUrl,
    getFullPageBoxBackground,
    getFullPageBoxBackgroundSize,
    getShellBackgroundColor,
    getTopMenuBackgroundColor,
    getTopMenuLogotype,
    getTopMenuTextColor,
    getTopMenuTextHoverColor,
    hostnameStartsWith,
  ]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
